import type {
  CanonicalAccountRole,
  MvpAccountRole,
  PlatformRole,
  ScopedAccessLevel,
  User,
} from '@/types';
import type { Capability } from './capabilities';
import {
  isPlatformRole,
  isWorkspaceRole,
  normalizeAccountRole,
  normalizePlatformRole,
  normalizeWorkspaceRole,
  resolveMvpAccountRole,
} from './accountRoles';
import { hasScopedAccessLevel } from '@/types';
import {
  resolveRouteAuthority,
  type ResolvedRouteAuthority,
} from './routeAuthority';
import {
  surfaceFromRequestedPath,
  type WorkspaceReturnSurface,
} from '@/lib/navigation/returnTarget';

export type RoutePlane = 'platform' | 'customer' | 'investor';
export type RouteAuthorityMode =
  | 'authenticated'
  | 'account_capability'
  | 'workspace_surface'
  | 'resource_scoped'
  | 'platform_capability';

export interface CanonicalRouteRule {
  id: string;
  path: string;
  match: 'exact' | 'prefix';
  authorityMode?: RouteAuthorityMode;
  planes?: readonly RoutePlane[];
  allowedRoles?: readonly CanonicalAccountRole[];
  requiredAnyCapabilities?: readonly Capability[];
  surface?: WorkspaceReturnSurface;
  requiresContextResolver?: boolean;
}

/**
 * Canonical product-route authority. Query-string tabs inherit the authority
 * of their page. Resource-level authority remains server/RLS controlled.
 */
export const CANONICAL_ROUTE_REGISTRY: readonly CanonicalRouteRule[] = [
  {
    id: 'home',
    path: '/',
    match: 'exact',
    authorityMode: 'authenticated',
  },
  {
    id: 'onboarding',
    path: '/onboarding',
    match: 'exact',
    authorityMode: 'authenticated',
    planes: ['customer'],
  },
  {
    id: 'account-security',
    path: '/account/security',
    match: 'exact',
    authorityMode: 'authenticated',
  },
  {
    id: 'platform-team',
    path: '/platform/team',
    match: 'prefix',
    authorityMode: 'platform_capability',
    planes: ['platform'],
    requiredAnyCapabilities: ['users.view.global'],
  },
  {
    id: 'platform-access',
    path: '/platform/access',
    match: 'prefix',
    authorityMode: 'platform_capability',
    planes: ['platform'],
    requiredAnyCapabilities: ['platform.access.view'],
  },
  {
    id: 'platform-commercial',
    path: '/platform/commercial',
    match: 'prefix',
    authorityMode: 'platform_capability',
    planes: ['platform'],
    requiredAnyCapabilities: ['platform.commercial.view'],
  },
  {
    id: 'platform-customers',
    path: '/platform/customers',
    match: 'prefix',
    authorityMode: 'platform_capability',
    planes: ['platform'],
    requiredAnyCapabilities: ['platform.customers.view'],
  },
  {
    id: 'platform-operations',
    path: '/platform/operations',
    match: 'prefix',
    authorityMode: 'platform_capability',
    planes: ['platform'],
    requiredAnyCapabilities: ['platform.operations.view'],
  },
  {
    id: 'platform-security',
    path: '/platform/security',
    match: 'prefix',
    authorityMode: 'platform_capability',
    planes: ['platform'],
    requiredAnyCapabilities: ['security.overview.view'],
  },
  {
    id: 'platform-overview',
    path: '/platform',
    match: 'exact',
    authorityMode: 'platform_capability',
    planes: ['platform'],
    requiredAnyCapabilities: ['platform.control.view'],
  },
  {
    id: 'settings-capital-callback',
    path: '/settings/integrations/capital/callback',
    match: 'exact',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['security.integrations.manage'],
  },
  {
    id: 'settings-invitations',
    path: '/settings/invitations',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['users.invite', 'users.manage'],
  },
  {
    id: 'security-rbac-admin-legacy',
    path: '/security/rbac-admin',
    match: 'exact',
    authorityMode: 'account_capability',
    requiredAnyCapabilities: [
      'platform.roles.assign',
      'platform.roles.activate',
      'platform.roles.activate.approve',
      'system.roles.manage',
      'users.roles.change',
    ],
  },
  {
    id: 'settings-roles',
    path: '/settings/roles',
    match: 'prefix',
    authorityMode: 'account_capability',
    requiredAnyCapabilities: [
      'platform.roles.assign',
      'platform.roles.activate',
      'platform.roles.activate.approve',
      'system.roles.manage',
      'users.roles.change',
    ],
  },
  {
    id: 'settings-billing',
    path: '/settings/billing',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['system.settings.edit'],
  },
  // NOTE: /settings/finance removed in Phase A module deletion. Finance module permanently removed.
  {
    id: 'settings-branding',
    path: '/settings/branding',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['system.settings.edit'],
  },
  {
    id: 'settings',
    path: '/settings',
    match: 'exact',
    authorityMode: 'authenticated',
  },
  {
    id: 'client-portal-legacy',
    path: '/client',
    match: 'prefix',
    authorityMode: 'authenticated',
    planes: ['customer'],
    // Legacy Client Portal paths redirect to Company Portal (D2).
    // Entry is resolved by LegacyCompanyPortalRedirect (company_representative).
  },
  {
    id: 'investor',
    path: '/investor',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['investor'],
    // Migrated from allowedRoles capital path to capability-based.
    // CRITICAL: ProtectedRoute MUST validate investment relationship + explicit right
    requiredAnyCapabilities: ['investments.rights.view'],
    // Access gated by contextResolver ensuring user has active investment scope
  },
  {
    id: 'security',
    path: '/security',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['security.overview.view'],
  },
  {
    id: 'users',
    path: '/users',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'users',
    requiredAnyCapabilities: ['users.view.global', 'users.view.collaborators'],
  },
  {
    id: 'invitations',
    path: '/invitations',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['users.manage'],
  },
  {
    id: 'companies',
    path: '/companies',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'companies',
    requiredAnyCapabilities: ['companies.view'],
  },
  {
    id: 'escalations',
    path: '/execution/escalations',
    match: 'prefix',
    // Multi-project queue shell: Project Managers enter through their active
    // project relationship; the page and governed repository still filter
    // every case to the projects they may view.
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'projects',
    requiredAnyCapabilities: ['escalations.view'],
  },
  {
    id: 'project-analytics',
    path: '/analytics/project-manager',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['analytics.view.projects'],
  },
  {
    id: 'analytics',
    path: '/analytics',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: [
      'analytics.view.business',
      'analytics.view.projects',
      'analytics.view.users',
    ],
  },
  {
    // Legacy flat /projects entry is now ONLY a redirect stub
    // (LegacyWorkspaceSurfaceRedirect -> /workspaces/select). It renders no
    // customer data, so it is a permissive customer-plane surface; the real
    // workspace authority is enforced after the redirect by the
    // WorkspaceAuthorityBoundary + Stage-1 + resource-scoped decisions.
    // Exact match so deep links /projects/:projectId still fall through to the
    // capability-gated prefix rule below.
    id: 'projects-legacy-redirect',
    path: '/projects',
    match: 'exact',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'projects',
  },
  {
    id: 'project-detail',
    path: '/projects/',
    match: 'prefix',
    authorityMode: 'resource_scoped',
    planes: ['customer'],
    requiredAnyCapabilities: ['projects.view'],
    requiresContextResolver: true,
  },
  {
    id: 'projects',
    path: '/projects',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'projects',
    requiredAnyCapabilities: ['projects.view'],
  },
  {
    id: 'milestones',
    path: '/milestones',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'milestones',
    requiredAnyCapabilities: ['projects.view', 'analytics.view.projects'],
  },
  {
    // Legacy flat /tasks entry is now ONLY a redirect stub
    // (LegacyWorkspaceSurfaceRedirect -> /workspaces/select). Exact match so
    // /tasks/:taskId legacy deep links still fall through to the
    // capability-gated prefix rule below.
    id: 'tasks-legacy-redirect',
    path: '/tasks',
    match: 'exact',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'tasks',
  },
  {
    id: 'tasks',
    path: '/tasks',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'tasks',
    requiredAnyCapabilities: ['tasks.view'],
  },
  {
    // Flat reports is an intent route only. It resolves a validated workspace
    // before the scoped report surface applies its own data filtering.
    id: 'reports-legacy-redirect',
    path: '/reports',
    match: 'exact',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'reports',
  },
  {
    id: 'reports',
    path: '/reports',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'reports',
    requiredAnyCapabilities: ['reports.view'],
  },
  {
    id: 'assets',
    path: '/assets',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'assets',
    requiredAnyCapabilities: ['files.view'],
  },
  {
    id: 'documents',
    path: '/documents',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'documents',
    requiredAnyCapabilities: ['files.view'],
  },
  {
    id: 'calendar',
    path: '/calendar',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: ['calendar.view'],
  },
  {
    id: 'chat',
    path: '/chat',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    // ChatHub is a participant collaboration surface — same bar as Calendar.
    requiredAnyCapabilities: ['calendar.view', 'projects.view', 'investor.chat.access'],
  },
  {
    id: 'messages-legacy',
    path: '/messages',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    // Legacy alias for /chat; uses same capability
    requiredAnyCapabilities: ['calendar.view', 'projects.view', 'investor.chat.access'],
  },
  {
    id: 'dashboard',
    path: '/dashboard',
    match: 'exact',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'dashboard',
    // Migrated from allowedRoles CUSTOMER_DASHBOARD_ROLES to capability-based
    requiredAnyCapabilities: ['analytics.view.business', 'analytics.view.projects'],
    // ProtectedRoute MUST extract workspaceId via contextResolver to prevent
    // silent workspace substitution
  },
  {
    id: 'investment-rights-workspace',
    path: '/workspace/investments',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'investments',
    requiredAnyCapabilities: ['investments.rights.view'],
  },
  {
    id: 'workspace-action',
    path: '/workspace',
    match: 'prefix',
    authorityMode: 'account_capability',
    planes: ['customer'],
    requiredAnyCapabilities: [
      'projects.view',
      'tasks.view',
      'files.view',
      'gates.view',
    ],
  },
  {
    // Route-derived workspace surfaces (/workspaces/:workspaceId/...). Registry
    // only admits the CUSTOMER PLANE here (platform accounts denied); the real
    // gating is the WorkspaceAuthorityBoundary (membership) + Stage-1
    // canResolve('workspace.*.list') + resource-scoped decisions. No flat
    // capability requirement — organization_member alone still enters nothing
    // beyond what the boundary/Stage-1 allow.
    id: 'workspaces-surface',
    path: '/workspaces',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
  },
  // NOTE: /capital is a gateway alias for Milestones (capital workflow entry point)
  // /system redirects to /analytics?tab=system (Command Center consolidation, not deletion)
  // /incident-reports redirects to /security?tab=incidents (Security Center consolidation)
  // All three remain active for backward compatibility and direct URL access
  {
    id: 'capital-legacy',
    path: '/capital',
    match: 'prefix',
    authorityMode: 'workspace_surface',
    planes: ['customer'],
    surface: 'milestones',
    requiredAnyCapabilities: ['projects.view', 'analytics.view.projects'],
    // Gateway alias for Milestones / capital workflows. Prefer direct
    // workspace-based routes, but this remains active for backward compatibility.
  },
  {
    id: 'system-legacy',
    path: '/system',
    match: 'prefix',
    planes: ['customer'],
    requiredAnyCapabilities: ['analytics.view.system'],
    // Legacy redirect to /analytics?tab=system. Redirect happens in Route.
    // This registry entry ensures the old URL doesn't fail auth checks.
  },
  {
    id: 'incident-reports-legacy',
    path: '/incident-reports',
    match: 'prefix',
    requiredAnyCapabilities: ['security.incidents.view'],
    // Legacy redirect to /security?tab=incidents. Part of Security Center
    // consolidation, not module deletion. Incident management remains active.
  },
  {
    id: 'ai-quality-internal',
    path: '/ai-quality',
    match: 'prefix',
    requiredAnyCapabilities: ['analytics.view.system'],
  },
  {
    id: 'debug-internal',
    path: '/debug',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'diagnostic-internal',
    path: '/diagnostic',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'auth-test-internal',
    path: '/auth-test',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'test-data-internal',
    path: '/test-data',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'storage-debug-internal',
    path: '/storage-debug',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'component-tests-internal',
    path: '/test',
    match: 'exact',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'e2e-login-internal',
    path: '/e2e-login',
    match: 'exact',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'progress-tests-internal',
    path: '/progress-test',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'debug-progress-internal',
    path: '/debug-progress',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'design-system-internal',
    path: '/design-system',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'chat-diagnostic-internal',
    path: '/chat-diagnostic',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'simple-chat-internal',
    path: '/simple-chat',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'tab-showcase-internal',
    path: '/tab-showcase',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'error-examples-internal',
    path: '/error-handling-examples',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'toast-test-internal',
    path: '/toast-test',
    match: 'prefix',
    requiredAnyCapabilities: ['debug.view'],
  },
  {
    id: 'portal-client-legacy',
    path: '/portal/client',
    match: 'exact',
    planes: ['customer'],
    // Migrated from allowedRoles portal path to capability-based (legacy path)
    requiredAnyCapabilities: ['projects.view'],
  },
  {
    id: 'partners',
    path: '/partners',
    match: 'prefix',
    authorityMode: 'authenticated',
    planes: ['customer'],
    // Partner access is proven by the canonical workspace route authority.
  },
  {
    id: 'vendors',
    path: '/vendors',
    match: 'prefix',
    authorityMode: 'authenticated',
    planes: ['customer'],
    // Vendor access is proven by the canonical workspace route authority.
  },
  {
    id: 'partner-portal-legacy',
    path: '/portal/partner',
    match: 'exact',
    authorityMode: 'authenticated',
    planes: ['customer'],
    // Legacy entry → Partner Portal (E1). Not Operating Projects.
    // Authenticated only — LegacyPartnerPortalRedirect proves partner_representative.
  },
  {
    id: 'vendor-portal-legacy',
    path: '/portal/vendor',
    match: 'exact',
    authorityMode: 'authenticated',
    planes: ['customer'],
    // Legacy entry → Vendor Portal (E2). Not Operating Projects.
    // Authenticated only — LegacyVendorPortalRedirect proves vendor_representative.
  },
  {
    id: 'rbac-admin-legacy',
    path: '/rbac-admin',
    match: 'exact',
    requiredAnyCapabilities: [
      'platform.roles.assign',
      'platform.roles.activate',
      'platform.roles.activate.approve',
      'system.roles.manage',
      'users.roles.change',
    ],
  },
  {
    id: 'due-diligence',
    path: '/dd-framework',
    match: 'prefix',
    planes: ['customer'],
    requiredAnyCapabilities: ['security.compliance.view', 'projects.view'],
  },
  {
    id: 'account-lifecycle',
    path: '/verify-email-change',
    match: 'prefix',
  },
  { id: 'data-handling', path: '/data-handling', match: 'prefix' },
  { id: 'docs', path: '/docs', match: 'prefix' },
  { id: 'unauthorized', path: '/unauthorized', match: 'exact' },
  { id: 'not-found', path: '/404', match: 'exact' },
] as const;

export interface RouteAccessContext {
  role: CanonicalAccountRole;
  capabilities: readonly Capability[];
  path: string;
}

export interface MvpRouteAccessContext {
  role: MvpAccountRole | null;
  capabilities: readonly Capability[];
  path: string;
  currentWorkspaceId?: string | null;
  resource?: {
    workspaceId: string;
    accessLevel: ScopedAccessLevel;
    requiredAccessLevel: ScopedAccessLevel;
    entitled: boolean;
  } | null;
}

export interface RouteAccessDecision {
  allowed: boolean;
  registered: boolean;
  rule?: CanonicalRouteRule;
  reason?: string;
}

export function findCanonicalRouteRule(path: string): CanonicalRouteRule | undefined {
  return [...CANONICAL_ROUTE_REGISTRY]
    .sort((a, b) => b.path.length - a.path.length || (a.match === b.match ? 0 : a.match === 'exact' ? -1 : 1))
    .find(candidate => matchesRoute(candidate, path));
}

function getRuleAuthorityMode(rule: CanonicalRouteRule): RouteAuthorityMode {
  return rule.authorityMode ?? 'account_capability';
}

function getRouteSurface(rule: CanonicalRouteRule, path: string): WorkspaceReturnSurface | null {
  return rule.surface ?? surfaceFromRequestedPath(path);
}

export function getAuthorityBackedRouteAccess(
  authority: ResolvedRouteAuthority,
  path: string,
  options?: Pick<MvpRouteAccessContext, 'currentWorkspaceId' | 'resource'>,
): RouteAccessDecision {
  const rule = findCanonicalRouteRule(path);
  if (!rule) return { allowed: false, registered: false, reason: 'unregistered_route' };

  const isPlatform = authority.platformRole !== null;
  const mode = getRuleAuthorityMode(rule);

  if (!authority.user) {
    return { allowed: false, registered: true, rule, reason: 'authentication_required' };
  }

  if (isPlatform) {
    if (rule.planes && !rule.planes.includes('platform')) {
      return { allowed: false, registered: true, rule, reason: 'plane_mismatch' };
    }
  } else {
    if (rule.planes?.includes('platform') || rule.planes?.includes('investor')) {
      return { allowed: false, registered: true, rule, reason: 'plane_mismatch' };
    }
  }

  switch (mode) {
    case 'authenticated':
      return { allowed: true, registered: true, rule };
    case 'platform_capability': {
      const allowed = Boolean(
        authority.platformRole
        && (!rule.requiredAnyCapabilities
          || rule.requiredAnyCapabilities.some((capability) => authority.capabilities.includes(capability))),
      );
      return {
        allowed,
        registered: true,
        rule,
        reason: allowed ? undefined : 'missing_platform_capability',
      };
    }
    case 'workspace_surface': {
      if (!authority.accountRole || !authority.hasAccessibleWorkspace) {
        return { allowed: false, registered: true, rule, reason: 'workspace_surface_unavailable' };
      }
      const surface = getRouteSurface(rule, path) ?? 'dashboard';
      const allowed = authority.surfaceEntries[surface] ?? false;
      return {
        allowed,
        registered: true,
        rule,
        reason: allowed ? undefined : `workspace_surface_denied:${surface}`,
      };
    }
    case 'resource_scoped': {
      const resource = options?.resource;
      const currentWorkspaceId = options?.currentWorkspaceId;
      if (!resource || !currentWorkspaceId) {
        return { allowed: false, registered: true, rule, reason: 'resource_context_required' };
      }
      const allowed = resource.workspaceId === currentWorkspaceId
        && resource.entitled
        && hasScopedAccessLevel(resource.accessLevel, resource.requiredAccessLevel);
      return {
        allowed,
        registered: true,
        rule,
        reason: allowed ? undefined : 'resource_scope_denied',
      };
    }
    case 'account_capability':
    default: {
      const allowed = !rule.requiredAnyCapabilities
        || rule.requiredAnyCapabilities.some((capability) => authority.capabilities.includes(capability));
      return {
        allowed,
        registered: true,
        rule,
        reason: allowed ? undefined : 'missing_account_capability',
      };
    }
  }
}

export function getRolePlane(role: CanonicalAccountRole): RoutePlane {
  if (isPlatformRole(role)) return 'platform';
  // capital portal path is a scoped relationship, not account-plane membership
  // Account-plane memberships always map to 'customer' plane; capital portal access determined by scoped context
  return 'customer';
}

function matchesRoute(rule: CanonicalRouteRule, path: string): boolean {
  if (rule.match === 'exact') return path === rule.path;
  if (rule.path.endsWith('/')) return path.startsWith(rule.path);
  return path === rule.path || path.startsWith(`${rule.path}/`);
}

export function getCanonicalRouteAccess(
  context: RouteAccessContext,
): RouteAccessDecision {
  const rule = [...CANONICAL_ROUTE_REGISTRY]
    .sort((a, b) => b.path.length - a.path.length || (a.match === b.match ? 0 : a.match === 'exact' ? -1 : 1))
    .find(candidate => matchesRoute(candidate, context.path));

  // Fail closed when a new authenticated route has not been added to the
  // registry. Layout sends this case to the canonical 404 before mounting it.
  if (!rule) return { allowed: false, registered: false };

  const plane = getRolePlane(context.role);
  if (rule.planes && !rule.planes.includes(plane)) {
    return { allowed: false, registered: true, rule };
  }

  if (rule.allowedRoles && !rule.allowedRoles.includes(context.role)) {
    return { allowed: false, registered: true, rule };
  }

  if (
    rule.requiredAnyCapabilities
    && !rule.requiredAnyCapabilities.some(capability =>
      context.capabilities.includes(capability)
    )
  ) {
    return { allowed: false, registered: true, rule };
  }

  return { allowed: true, registered: true, rule };
}

export function canAccessCanonicalRoute(
  role: CanonicalAccountRole,
  capabilities: readonly Capability[],
  path: string,
): boolean {
  return getCanonicalRouteAccess({ role, capabilities, path }).allowed;
}

export function getMvpRouteAccess(context: MvpRouteAccessContext): RouteAccessDecision {
  if (!context.role) return { allowed: false, registered: true };
  const rule = [...CANONICAL_ROUTE_REGISTRY]
    .sort((a, b) => b.path.length - a.path.length || (a.match === b.match ? 0 : a.match === 'exact' ? -1 : 1))
    .find(candidate => matchesRoute(candidate, context.path));
  if (!rule) return { allowed: false, registered: false };

  const isPlatform = context.role === 'platform_owner'
    || context.role === 'platform_admin'
    || context.role === 'support_operator';
  if (isPlatform) {
    if (rule.planes && !rule.planes.includes('platform')) {
      return { allowed: false, registered: true, rule };
    }
    const allowed = !rule.requiredAnyCapabilities
      || rule.requiredAnyCapabilities.some(capability => context.capabilities.includes(capability));
    return { allowed, registered: true, rule };
  }

  if (rule.planes?.includes('platform') || rule.planes?.includes('investor')) {
    return { allowed: false, registered: true, rule };
  }
  if (rule.allowedRoles) {
    return { allowed: false, registered: true, rule };
  }
  const needsResourceScope = /^\/(projects?|investments?|investor\/reports)\//.test(context.path);
  if (!needsResourceScope) {
    if (
      rule.requiredAnyCapabilities
      && !rule.requiredAnyCapabilities.some(capability => context.capabilities.includes(capability))
    ) {
      return { allowed: false, registered: true, rule };
    }
    return { allowed: true, registered: true, rule };
  }
  const resource = context.resource;
  if (!resource || !context.currentWorkspaceId) return { allowed: false, registered: true, rule };
  return {
    allowed: resource.workspaceId === context.currentWorkspaceId
      && resource.entitled
      && hasScopedAccessLevel(resource.accessLevel, resource.requiredAccessLevel),
    registered: true,
    rule,
  };
}

export function resolveUserAccountRole(
  user: Pick<User, 'account_role' | 'platform_role' | 'workspace_role' | 'workspace_id' | 'role'> | null | undefined,
): CanonicalAccountRole | null {
  if (user?.account_role) {
    const accountRole = normalizeAccountRole(user.account_role);
    if (accountRole) return accountRole;
  }
  if (user?.platform_role) {
    const platformRole = normalizePlatformRole(user.platform_role);
    if (platformRole) return platformRole;
  }
  if (user?.workspace_role && isWorkspaceRole(user.workspace_role)) {
    return user.workspace_role;
  }
  // During legacy-value migration, prefer an already-canonical account role
  // over a stale workspace_role alias such as "member".
  if (user?.role && (isWorkspaceRole(user.role) || isPlatformRole(user.role))) {
    return normalizeAccountRole(user.role);
  }
  if (user?.workspace_role) {
    const workspaceRole = normalizeWorkspaceRole(user.workspace_role);
    if (workspaceRole) return workspaceRole;
  }
  if (user?.workspace_id) {
    const workspaceRole = normalizeWorkspaceRole(user.role);
    if (workspaceRole) return workspaceRole;
  }
  return normalizeAccountRole(user?.role);
}

export function resolveUserMvpAccountRole(
  user: Pick<User, 'account_role'> | null | undefined,
): MvpAccountRole | null {
  return resolveMvpAccountRole(user?.account_role);
}

export function getUserRouteAccess(
  user: Pick<User, 'account_role' | 'platform_role' | 'workspace_role' | 'workspace_id' | 'role'> | null | undefined,
  path: string,
): RouteAccessDecision {
  return getAuthorityBackedRouteAccess(resolveRouteAuthority(user as User | null | undefined), path);
}

export function getCanonicalRoutePath(
  routeId: CanonicalRouteRule['id'],
): string {
  const rule = CANONICAL_ROUTE_REGISTRY.find(candidate =>
    candidate.id === routeId
  );
  if (!rule) throw new Error(`Unknown canonical route: ${routeId}`);
  return rule.path;
}

export const PLATFORM_ROUTE_ROLES: readonly PlatformRole[] = [
  'platform_owner',
  'system_admin',
  'security_admin',
  'support_admin',
  'commercial_admin',
  'implementation_admin',
];
